import { useEffect, useState } from 'react';

// Capture the `beforeinstallprompt` event at module scope so it survives
// component unmounts (e.g., user dismisses the banner, then clicks the
// sidebar "Install app" button). Chrome fires this event only once per
// page load, so we must not lose it.
let deferredPrompt = null;
let installed = false;
const listeners = new Set();

const notify = () => listeners.forEach((cb) => cb());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    installed = true;
    deferredPrompt = null;
    notify();
  });
}

const isStandalone = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true);

const detectIOS = () => {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
};

/**
 * Shared install-app state.
 * Returns:
 *   canPromptNatively – true when we have a captured beforeinstallprompt event
 *   isIOS – true on iPhone/iPad (needs Share → Add to Home Screen)
 *   isStandalone – already installed / launched from home screen
 *   promptInstall() – triggers native install dialog; returns 'accepted' | 'dismissed' | null
 */
export const useInstallPrompt = () => {
  const [, setTick] = useState(0);

  useEffect(() => {
    const cb = () => setTick((n) => n + 1);
    listeners.add(cb);
    return () => listeners.delete(cb);
  }, []);

  const promptInstall = async () => {
    if (!deferredPrompt) return null;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    // Chrome consumes the event once prompted; drop our reference.
    deferredPrompt = null;
    notify();
    return choice?.outcome || null;
  };

  return {
    canPromptNatively: !!deferredPrompt && !installed,
    isIOS: detectIOS(),
    isStandalone: isStandalone() || installed,
    promptInstall,
  };
};
